import React, { useState, useCallback } from 'react';
import ItemsDropdown from 'components/ItemsDropdown';
import CompanyIcon from 'assets/company.svg?react';
import PersonIcon from 'assets/person.svg?react';
import PlusIcon from 'assets/plus.svg?react';
import AddNewCompanyPeopleDrawer from '../AddNewCompanyPeopleDrawer';
import CompanyPeopleDropDownItem from '../CompanyPeopleDropDownItem';
import { dropdownFilterHandler } from '../../helpers/dropdown-utils';
import {
  CompanyPeopleDropdownBaseProps,
  CompanyPeopleDropDownItemProps,
  CompanyPeopleItem
} from '../../types';

/**
 * Creates a value renderer function for the dropdown
 * @param value - The selected value
 * @param allowSearch - Whether search is allowed
 * @returns The value renderer function or null
 */
const createValueRenderer = (
  value: CompanyPeopleItem | null,
  allowSearch?: boolean,
  hideIcon: boolean = false
): React.ReactNode | string | null => {
  if (!value?.type) {
    return null;
  }

  if (allowSearch) {
    return value.name;
  }

  return (
    <div
      className="w-full h-full p-2 flex items-center"
      data-testid="selected-company-people"
    >
      {!hideIcon && (
        <span>
          {value?.type === 'People' ? <PersonIcon /> : <CompanyIcon />}
        </span>
      )}
      <div className="pl-2">{value.name}</div>
    </div>
  );
};

/**
 * Base component for CompanyPeopleDropdown
 * Handles rendering the dropdown and drawer
 */
const CompanyPeopleDropdownBase: React.FC<CompanyPeopleDropdownBaseProps> = ({
  bottomButtonText,
  bottomButtonIcon = <PlusIcon />,
  onChange,
  value,
  id,
  title,
  allowSearch,
  items,
  defaultDrawerType,
  disableDrawerTypeChange,
  hideIcon
}) => {
  const [
    addNewCompanyPeopleDrawerOpen,
    setAddNewCompanyPeopleDrawerOpen
  ] = useState(false);
  const valueRenderer = useCallback(
    () => createValueRenderer(value, allowSearch, hideIcon),
    [value, allowSearch, hideIcon]
  );

  const ItemComponent = useCallback(
    (props: { item: CompanyPeopleDropDownItemProps['item'] }) => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <CompanyPeopleDropDownItem {...props} hideIcon={hideIcon} />
    ),
    [hideIcon]
  );

  return (
    <>
      <ItemsDropdown
        rightOffset={0}
        heightClass="h-64"
        valueRenderer={valueRenderer}
        value={value}
        bottomButtonText={bottomButtonText}
        bottomButtonIcon={bottomButtonIcon}
        onBottomButtonClick={() => {
          setAddNewCompanyPeopleDrawerOpen(true);
        }}
        onChange={onChange}
        id={id}
        items={items}
        ItemComponent={ItemComponent}
        mergeSearchInputWithValue={allowSearch}
        searchPlaceholderText=""
        onFilter={allowSearch ? dropdownFilterHandler : undefined}
      />
      {addNewCompanyPeopleDrawerOpen && (
        <AddNewCompanyPeopleDrawer
          onClose={() => setAddNewCompanyPeopleDrawerOpen(false)}
          onCompanyPeopleAdded={onChange}
          open={addNewCompanyPeopleDrawerOpen}
          title={title}
          defaultType={defaultDrawerType}
          disableTypeChange={disableDrawerTypeChange}
        />
      )}
    </>
  );
};

export default CompanyPeopleDropdownBase;
